import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

interface NotificationPreferencesProps {
  projectId: number;
  projectName: string;
}

interface Preferences {
  notifyJobChanges: boolean;
  notifyOpenToWork: boolean;
  notifyFlightRisk: boolean;
  notifyProfileChanges: boolean;
  notifyCompanyTrends: boolean;
  instantAlerts: boolean;
  weeklyDigest: boolean;
  digestDay: number;
}

interface Recipient {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  teamRole: string;
  isOwner?: boolean;
  isRecipient: boolean;
  instantAlerts: boolean;
  weeklyDigest: boolean;
  jobDailyDigest: boolean;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_ABBR = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

type DigestFrequency = 'daily' | 'weekly' | 'fortnightly' | 'monthly';
interface DigestSchedule {
  frequency: DigestFrequency;
  daysOfWeek: number[];
  dayOfMonth: number | null;
}
const FREQUENCY_LABELS: { value: DigestFrequency; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];

const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({ projectId, projectName }) => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Preferences>({
    notifyJobChanges: true,
    notifyOpenToWork: true,
    notifyFlightRisk: true,
    notifyProfileChanges: false,
    notifyCompanyTrends: true,
    instantAlerts: true,
    weeklyDigest: true,
    digestDay: 1
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [digestSchedules, setDigestSchedules] = useState<DigestSchedule[]>([]);

  const isTeamUser = !!user?.teamId;
  const canManageRecipients = user?.teamRole === 'admin';

  useEffect(() => {
    fetchPreferences();
    fetchDigestSchedules();
    if (isTeamUser) fetchRecipients();
  }, [projectId]);

  const fetchDigestSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/notifications/projects/${projectId}/digest-schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setDigestSchedules(await res.json());
    } catch (err) {
      console.error('Failed to fetch digest schedules:', err);
    }
  };

  const saveDigestSchedules = async (next: DigestSchedule[]) => {
    setDigestSchedules(next);
    setSaving(true);
    setSaved(false);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/notifications/projects/${projectId}/digest-schedules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ schedules: next })
      });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    } catch (err) {
      console.error('Failed to save digest schedules:', err);
    } finally {
      setSaving(false);
    }
  };

  const addSchedule = () =>
    saveDigestSchedules([...digestSchedules, { frequency: 'weekly', daysOfWeek: [1], dayOfMonth: null }]);
  const removeSchedule = (idx: number) =>
    saveDigestSchedules(digestSchedules.filter((_, i) => i !== idx));
  const updateSchedule = (idx: number, patch: Partial<DigestSchedule>) =>
    saveDigestSchedules(digestSchedules.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const changeFrequency = (idx: number, freq: DigestFrequency) => {
    const cur = digestSchedules[idx];
    if (freq === 'monthly') {
      updateSchedule(idx, { frequency: freq, daysOfWeek: [], dayOfMonth: cur.dayOfMonth || 1 });
    } else if (freq === 'daily') {
      updateSchedule(idx, { frequency: freq, daysOfWeek: [], dayOfMonth: null });
    } else {
      updateSchedule(idx, { frequency: freq, daysOfWeek: cur.daysOfWeek.length ? cur.daysOfWeek : [1], dayOfMonth: null });
    }
  };

  const toggleDay = (idx: number, day: number) => {
    const cur = digestSchedules[idx];
    const has = cur.daysOfWeek.includes(day);
    // Keep at least one day for weekly/fortnightly.
    if (has && cur.daysOfWeek.length === 1) return;
    const days = has ? cur.daysOfWeek.filter(d => d !== day) : [...cur.daysOfWeek, day].sort((a, b) => a - b);
    updateSchedule(idx, { daysOfWeek: days });
  };

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/notifications/projects/${projectId}/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPrefs(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch notification preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/notifications/projects/${projectId}/recipients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecipients(data.recipients);
      }
    } catch (err) {
      console.error('Failed to fetch notification recipients:', err);
    }
  };

  const savePreferences = async (updatedPrefs: Preferences) => {
    setSaving(true);
    setSaved(false);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/notifications/projects/${projectId}/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updatedPrefs)
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      console.error('Failed to save notification preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const updatePref = (key: keyof Preferences, value: any) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    savePreferences(updated);
  };

  const toggleRecipient = (userId: number, field: 'instantAlerts' | 'weeklyDigest' | 'jobDailyDigest') => {
    setRecipients(prev => prev.map(r => {
      if (r.userId !== userId) return r;
      const updated = { ...r, [field]: !r[field] };
      // If both are off, mark as not a recipient
      if (!updated.instantAlerts && !updated.weeklyDigest && !updated.jobDailyDigest) {
        updated.isRecipient = false;
      } else {
        updated.isRecipient = true;
      }
      return updated;
    }));
  };

  const toggleRecipientAll = (userId: number) => {
    setRecipients(prev => prev.map(r => {
      if (r.userId !== userId) return r;
      const shouldEnable = !r.isRecipient;
      return {
        ...r,
        isRecipient: shouldEnable,
        instantAlerts: shouldEnable,
        weeklyDigest: shouldEnable,
        jobDailyDigest: shouldEnable
      };
    }));
  };

  const saveRecipients = async () => {
    setSavingRecipients(true);
    try {
      const token = localStorage.getItem('token');
      // Always include the owner in the payload (even when toggled fully off) so
      // the backend can persist an explicit "off" — the owner is on by default,
      // so omitting them would silently re-enable their alerts.
      const activeRecipients = recipients
        .filter(r => r.isRecipient || r.isOwner)
        .map(r => ({
          userId: r.userId,
          instantAlerts: r.instantAlerts,
          weeklyDigest: r.weeklyDigest,
          jobDailyDigest: r.jobDailyDigest
        }));

      const res = await fetch(`/api/notifications/projects/${projectId}/recipients`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ recipients: activeRecipients })
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      console.error('Failed to save notification recipients:', err);
    } finally {
      setSavingRecipients(false);
    }
  };

  if (loading) {
    return <div className="text-gray-400 text-sm py-4">Loading notification preferences...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Signal Types */}
      <div>
        <h4 className="text-sm font-semibold text-gray-200 mb-3">What to notify you about</h4>
        <div className="space-y-3">
          <SignalToggle
            label="Job Changes"
            description="When someone changes company or role - key for backfill opportunities"
            checked={prefs.notifyJobChanges}
            onChange={(v) => updatePref('notifyJobChanges', v)}
            priority="high"
          />
          <SignalToggle
            label="Open to Work"
            description="When a candidate marks themselves as open to new opportunities"
            checked={prefs.notifyOpenToWork}
            onChange={(v) => updatePref('notifyOpenToWork', v)}
            priority="high"
          />
          <SignalToggle
            label="Flight Risk Alerts"
            description="When a candidate shows high or critical flight risk signals"
            checked={prefs.notifyFlightRisk}
            onChange={(v) => updatePref('notifyFlightRisk', v)}
            priority="high"
          />
          <SignalToggle
            label="Company Hiring Trends"
            description="When a company shows significant hiring or attrition patterns"
            checked={prefs.notifyCompanyTrends}
            onChange={(v) => updatePref('notifyCompanyTrends', v)}
            priority="medium"
          />
          <SignalToggle
            label="Profile Updates"
            description="Headline, skills, or summary changes (can be frequent)"
            checked={prefs.notifyProfileChanges}
            onChange={(v) => updatePref('notifyProfileChanges', v)}
            priority="low"
          />
        </div>
      </div>

      {/* Delivery Method */}
      <div>
        <h4 className="text-sm font-semibold text-gray-200 mb-3">How to be notified</h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <div>
              <p className="text-sm font-medium text-gray-200">Instant Email Alerts</p>
              <p className="text-xs text-gray-400">Immediate email for job changes, open-to-work, and flight risk signals</p>
            </div>
            <Toggle checked={prefs.instantAlerts} onChange={(v) => updatePref('instantAlerts', v)} />
          </div>
          <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium text-gray-200">Digest Emails</p>
              <button
                onClick={addSchedule}
                className="text-xs text-blue-400 hover:text-blue-300 font-medium"
              >
                + Add digest
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-3">
              A summary of signals on your schedule. Add more than one — e.g. a daily and a weekly.
            </p>

            {digestSchedules.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No digest emails set up.</p>
            ) : (
              <div className="space-y-2">
                {digestSchedules.map((s, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2 p-2 bg-slate-900/40 rounded border border-slate-700/60">
                    <select
                      value={s.frequency}
                      onChange={(e) => changeFrequency(idx, e.target.value as DigestFrequency)}
                      className="text-xs bg-slate-700 border border-slate-600 text-gray-200 rounded px-2 py-1"
                    >
                      {FREQUENCY_LABELS.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>

                    {/* Day picker per frequency */}
                    {(s.frequency === 'weekly' || s.frequency === 'fortnightly') && (
                      <div className="flex items-center gap-1">
                        {s.frequency === 'weekly' && <span className="text-[11px] text-gray-500">on</span>}
                        {DAY_ABBR.map((abbr, day) => {
                          const active = s.daysOfWeek.includes(day);
                          return (
                            <button
                              key={day}
                              onClick={() => toggleDay(idx, day)}
                              className={`w-6 h-6 rounded-full text-[10px] font-medium ${
                                active ? 'bg-blue-600 text-white' : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                              }`}
                              title={DAYS[day]}
                            >
                              {abbr}
                            </button>
                          );
                        })}
                        {s.frequency === 'weekly' && (
                          <span className="text-[11px] text-gray-500 ml-1">
                            {s.daysOfWeek.length > 1 ? `(${s.daysOfWeek.length}× / week)` : ''}
                          </span>
                        )}
                      </div>
                    )}

                    {s.frequency === 'monthly' && (
                      <div className="flex items-center gap-1 text-[11px] text-gray-400">
                        on day
                        <input
                          type="number"
                          min={1}
                          max={28}
                          value={s.dayOfMonth ?? 1}
                          onChange={(e) => {
                            const v = Math.min(28, Math.max(1, parseInt(e.target.value) || 1));
                            updateSchedule(idx, { dayOfMonth: v });
                          }}
                          className="w-14 text-xs bg-slate-700 border border-slate-600 text-gray-200 rounded px-2 py-1"
                        />
                        of the month
                      </div>
                    )}

                    {s.frequency === 'daily' && (
                      <span className="text-[11px] text-gray-500">every morning</span>
                    )}

                    <button
                      onClick={() => removeSchedule(idx)}
                      className="ml-auto text-gray-500 hover:text-red-400 text-sm px-1"
                      title="Remove this digest"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Team Recipients */}
      {isTeamUser && recipients.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-200 mb-1">Who gets notified</h4>
          <p className="text-xs text-gray-400 mb-3">
            Choose who receives notifications for this project. The project owner is
            included by default — untick them to stop their alerts.
          </p>
          <div className="space-y-2">
            {recipients.map(r => (
              <div key={r.userId} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => canManageRecipients && toggleRecipientAll(r.userId)}
                    disabled={!canManageRecipients}
                    className={`w-5 h-5 rounded border flex items-center justify-center text-xs transition-colors ${
                      r.isRecipient
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : 'bg-slate-700 border-slate-500'
                    } ${!canManageRecipients ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {r.isRecipient && '✓'}
                  </button>
                  <div>
                    <p className="text-sm font-medium text-gray-200 flex items-center gap-2">
                      {r.firstName} {r.lastName}
                      {r.isOwner && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-500/20 text-violet-300">
                          Owner
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">{r.email}</p>
                  </div>
                </div>
                {r.isRecipient && canManageRecipients && (
                  <div className="flex items-center gap-4 text-xs">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={r.instantAlerts}
                        onChange={() => toggleRecipient(r.userId, 'instantAlerts')}
                        className="rounded border-slate-500 text-emerald-500 focus:ring-emerald-500 bg-slate-700"
                      />
                      <span className="text-gray-300">Instant</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={r.weeklyDigest}
                        onChange={() => toggleRecipient(r.userId, 'weeklyDigest')}
                        className="rounded border-slate-500 text-emerald-500 focus:ring-emerald-500 bg-slate-700"
                      />
                      <span className="text-gray-300">Digest</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer" title="Daily email of new job vacancies found at tracked companies. Also controls whether this project's jobs appear on their dashboard.">
                      <input
                        type="checkbox"
                        checked={r.jobDailyDigest}
                        onChange={() => toggleRecipient(r.userId, 'jobDailyDigest')}
                        className="rounded border-slate-500 text-sky-500 focus:ring-sky-500 bg-slate-700"
                      />
                      <span className="text-gray-300">Jobs (daily)</span>
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
          {canManageRecipients && (
            <button
              onClick={saveRecipients}
              disabled={savingRecipients}
              className="mt-3 px-4 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded transition-colors disabled:opacity-50"
            >
              {savingRecipients ? 'Saving...' : 'Save recipients'}
            </button>
          )}
        </div>
      )}

      {/* Save indicator */}
      {saved && (
        <p className="text-xs text-emerald-400 text-center">Preferences saved</p>
      )}
    </div>
  );
};

// Toggle switch component
const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
  <button
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
      checked ? 'bg-emerald-500' : 'bg-slate-600'
    }`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-4.5' : 'translate-x-0.5'
      }`}
      style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }}
    />
  </button>
);

// Signal type toggle with priority indicator
const SignalToggle: React.FC<{
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  priority: 'high' | 'medium' | 'low';
}> = ({ label, description, checked, onChange, priority }) => {
  const priorityColors = {
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
  };

  return (
    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
      <div className="flex items-center gap-3">
        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${priorityColors[priority]}`}>
          {priority}
        </span>
        <div>
          <p className="text-sm font-medium text-gray-200">{label}</p>
          <p className="text-xs text-gray-400">{description}</p>
        </div>
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
};

export default NotificationPreferences;
